import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {NavLink, useLocation} from "react-router-dom";
import {getCookie} from "react-use-cookie";

import {API_ENDPOINTS, BASE_PATH, NAV_PATH_NAMES} from "../../api/endpoints";
import {MODE} from "../../gitIgnore/appMode";
import {getCurrentUser, selectIsAuth} from "../../slices/UserSlice";
import FunctionButton from "../../utilities/Buttons/FunctionButton";
import SwitchCustom from "../../utilities/Buttons/SwitchComponent/SwitchCustom";
import Divider from "../../utilities/Divider/Divider";
import {
  CO_MANAGERS_POSITIONS_IDS,
  ICON_NAMES,
  IT_MANAGERS_IDS,
} from "../../utilities/generalConstants.js/generalConstants";
import ToolTip, {TOOLTIP_PLACEMENT} from "../../utilities/ToolTip";
import AccessBoundary from "../AccessBoundary/AccessBoundary";
import {UI_PERMISSION_TYPE} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";
import DevIT from "../DevIT/DevIT";
import {clearCurrentTicket, selectNewTickets} from "../DevIT/DevITSlice";
import Icon from "../Icon/Icon";
import KpFinHiddenColumns from "../KpFinHiddenColumns/KpFinHiddenColumns";
import {useModal} from "../Modal/ModalProvider";
import TranslationButton from "../Translation/TranslationButton";

import NavbarInfo from "./navbarUnits/NavbarInfo";
import MyLink from "./navbarUnits/NavbarLink";
import NavItem from "./navbarUnits/NavItem";

import "./src/NavBar.scss";

const NavBar = () => {
  const {t, i18n} = useTranslation();
  const location = useLocation();
  const {openModal} = useModal();
  const dispatch = useDispatch();

  const isAuth = useSelector(selectIsAuth);
  const token = getCookie("access_token");
  const user = useSelector(getCurrentUser);
  const isITManager = IT_MANAGERS_IDS.includes(user?.id);
  const isCoManager = isITManager || CO_MANAGERS_POSITIONS_IDS.includes(user?.position?.positionId);
  const devITNewTickets = useSelector(selectNewTickets);

  let activeStyle = {
    fontWeight: 600,
  };

  const resolveTitleByPath = path => {
    const map = {
      "/ba3a/main": "ERP 'ba3a'",

      //? adm
      "/ba3a/admin/addPerson": t("Person"),
      "/ba3a/admin/registr": t("User"),
      "/ba3a/admin/addClient": t("Client"),
      "/ba3a/admin/settings": t("Settings (ba3a)"),

      //? proj
      "/ba3a/project": t("Project"),
      "/ba3a/project/projectDirectiveList": t("Payment directives list (CO)"),
      "/ba3a/project/projectInvoiceList": t("Invoice List"),
      "/ba3a/project/projectSettings": t("Settings (Project)"),

      //? kp
      "/ba3a/co": t("CO"),
      "/ba3a/co/templateRegister": t("TemplateRegister"),
      "/ba3a/co/control": t("Control PF"),

      //? sku
      "/ba3a/sku": "SKU",

      //? zed
      "/ba3a/fea/bos/request-registr": t("Request register"),
      "/ba3a/fea/orderProcess": t("Order processing"),
      "/ba3a/fea/reqInProgress": t("Requests in progress"),
      "/ba3a/fea/readyReq": t("req stat read"),
      "/ba3a/fea/paymentDirectiveList": t("Payment directive list (VED)"),
      "/ba3a/fea/factorySettings": t("factory cc"),
      "/ba3a/fea/factoryManagement": t("factory cc"),
      "/ba3a/fea/vedSettingsGeneralNew": t("Settings (VED)"),

      //? log
      "/ba3a/logistician/sleeping-requests": t("Request Register for planning"),
      "/ba3a/logistician/bos/tp-pool-preregister": t("Request Register (chief)"),
      "/ba3a/logistician/tp-pool-register": t("Request Register in progress"),
      "/ba3a/logistician/log-list-payment-to-tp": t("Payment directive list (TP)"),
      "/ba3a/logistician/log-list-payment-to-sp": t("Payment directive list (SP)"),
      "/ba3a/logistician/tp-pool-factory": t("Process block factory"),
      "/ba3a/logistician/tp-transportation": t("Transportation block (TP)"),
      "/ba3a/logistician/warehouse": t("Process block 'Warehouse'"),
      "/ba3a/logistician/bos/final-destination": t("Final Destination (Boss)"),
      "/ba3a/logistician/final-destination-Manager": t("Final destination (FD)"),
      "/ba3a/logistician/settings": t("Settings (Logist)"),

      //? finblock
      "/ba3a/fin-block/canceledRequests": t("Canceled requests register"),
      "/ba3a/fin-block/cashdeskDirective": t("Cashdesk directive"),
      "/ba3a/fin-block/clientDirectiveList": t("Directives list (Client)"),
      "/ba3a/fin-block/projectDirectiveList": t("Payment directives list (CO)"),
      "/ba3a/fin-block/exchangeRates": t("Exchange rates"),
      "/ba3a/fin-block/bankClient": t("Bank-Client"),
      "/ba3a/fin-block/finBlockInvoiceList": t("Invoice List"),
      "/ba3a/fin-block/finBlockTransactionDayList": t("Daily operations register"),
      "/ba3a/fin-block/paymentDirectiveList": t("Payment directive list (VED)"),
      "/ba3a/fin-block/paymentDirectiveListTP": t("Payment directive list (TP)"),
      "/ba3a/fin-block/paymentDirectiveListStatement": t("Payment directive list (SP)"),
      "/ba3a/fin-block/fin_settings": t("Settings (FinBlock)"),

      //? cashdesk
      [`/${NAV_PATH_NAMES.CASHDESK}/exchangeRates`]: `${t("Cashdesk")}: ${t("Exchange rates")}`,
      [`/${NAV_PATH_NAMES.CASHDESK}/directiveList`]: `${t("Cashdesk")}: ${t("Payment directives list (CO)")}`,
      [`/${NAV_PATH_NAMES.CASHDESK}/invoiceList`]: `${t("Cashdesk")}: ${t("Invoice List")}`,
      [`/${NAV_PATH_NAMES.CASHDESK}/cashdeskDirective`]: `${t("Cashdesk")}: ${t("Cashdesk directive")}`,
      [`/${NAV_PATH_NAMES.CASHDESK}/transactionDayList`]: `${t("Cashdesk")}: ${t("Daily operations register")}`,
      [`/${NAV_PATH_NAMES.CASHDESK}/project`]: `${t("Cashdesk")}: ${t("Project")}`,

      //? ticket
      "/ba3a/references/tasksList": t("Tasks list"),

      //? changesList
      "/ba3a/changeLog/changesList": t("Changes list"),

      //? baza docs
      "/ba3a/docs/bazaDocumentation": t("ba3a documentation"),

      //? baza docs
      [`/${BASE_PATH}/${API_ENDPOINTS.SYSTEM.BASE}/${API_ENDPOINTS.SYSTEM.ROUTINE_PROCEDURES}`]: `${t(
        "Routine procedures",
      )}`,
    };

    return map[path] || null;
  };

  useEffect(() => {
    const exactTitle = resolveTitleByPath(location.pathname);
    if (exactTitle) {
      document.title = `${exactTitle} / ${MODE}`;
      return;
    }

    //! фолбэк если нету маппинга ТЕСТ!!
    const last = location.pathname.split("/").filter(Boolean).pop() || "";
    document.title = `${last} / ${MODE}`;
  }, [location.pathname, t]);

  const showTranslateModal = () => {
    openModal(<TranslationButton />, {label: t("Language"), className: "smallModal"});
  };

  const showCOHiddenColumnsModal = () => {
    openModal(<KpFinHiddenColumns />, {
      label: t("CO(f) columns settings"),
      // style: {width: "100%", height: "70%"},
      closableOnEsc: false,
      className: "largeModal",
    });
  };

  const showDevItModal = () => {
    openModal(<DevIT />, {
      label: t("Appeal to dev IT"),
      closableOnEsc: false,
      style: {width: "90%", height: "85%"},
      className: "largeModal",
      onModalClose: () => dispatch(clearCurrentTicket()),
    });
  };

  const currentLang = (i18n.language || "en").toLowerCase();
  const isUa = currentLang.startsWith("uk");

  const handleLangToggle = () => {
    i18n.changeLanguage(isUa ? "en" : "uk");
  };

  return (
    <div className="navbar">
      <div className="navbar__container">
        <div className="row gap">
          <NavLink end to="/ba3a/main" style={({isActive}) => (isActive ? activeStyle : undefined)}>
            <ToolTip title="Go to Start page" placement={TOOLTIP_PLACEMENT.RIGHT_END}>
              <div className="navbar__logo">
                <Icon name={ICON_NAMES.LOGO} className="icon-logo" width={30} height={30} />
                <h1>ERP ’ba3a’ - {MODE}</h1>
              </div>
            </ToolTip>
          </NavLink>
          {isAuth && token && user && (
            <nav style={{paddingTop: "5px", paddingRight: "20px"}}>
              <NavLink to="/ba3a/changeLog/changesList" end>
                <ToolTip title="Changes list" placement={TOOLTIP_PLACEMENT.RIGHT_END}>
                  <Icon name={ICON_NAMES.CHANGE_LOG} width={20} height={20} />
                </ToolTip>
              </NavLink>
            </nav>
          )}
        </div>

        {isAuth && token && user && (
          <nav className="navbar__top_navigation fGrow jcc">
            <ul className="jcfe" style={{listStyle: "none"}}>
              <NavItem name="ba3a" dataName={NAV_PATH_NAMES.ADMIN}>
                {/* <MyLink to="/ba3a/admin/addPerson" name={t("Person")} />
                <AccessBoundary>
                  <MyLink to="/ba3a/admin/registr" name={t("User")} />
                </AccessBoundary>
                <MyLink to="/ba3a/admin/addClient" name={t("Client")} />
                <MyLink
                  // to="/xx"
                  name={t("Designer")}
                  notAvailableStyle
                /> */}
                <AccessBoundary>
                  <MyLink to="/ba3a/admin/settings" name={t("Settings (ba3a)")} />
                </AccessBoundary>
              </NavItem>

              <NavItem name={t("PROJECT")} dataName={NAV_PATH_NAMES.PROJECT}>
                <MyLink to="/ba3a/project" name={t("Project")} />
                <MyLink to="/ba3a/project/projectDirectiveList" name={t("Payment directives list (CO)")} />
                <MyLink to="/ba3a/project/projectInvoiceList" name={t("Invoice List")} />
                <Divider noMargin />
                <MyLink to="/ba3a/project/projectSettings" name={t("Settings (Project)")} />

                {/* <MyLink to="/ba3a/project/projectView" name={t("Project View")} /> */}
              </NavItem>

              <NavItem name={t("CO")} dataName={NAV_PATH_NAMES.CO}>
                <MyLink to="/ba3a/co" name={t("CO")} />
                <MyLink to="/ba3a/co/templateRegister" name={t("TemplateRegister")} />
                <MyLink to="/ba3a/co/control" name={t("Control PF")} />
                <MyLink name={t("CO register")} onlyForSU notAvailableStyle />
                <MyLink name={t("CO album")} notAvailableStyle onlyForSU />
                <MyLink name={t("Archive PF")} notAvailableStyle onlyForSU />
              </NavItem>
              <NavItem name="SKU" dataName={NAV_PATH_NAMES.SKU}>
                <MyLink to="/ba3a/sku" name="SKU" />
              </NavItem>
              <NavItem name={t("FEA")} dataName={NAV_PATH_NAMES.VED}>
                <MyLink to="/ba3a/fea/bos/request-registr" name={t("Request register")} />
                <MyLink to="/ba3a/fea/orderProcess" name={t("Order processing")} />
                <MyLink to="/ba3a/fea/reqInProgress" name={t("Requests in progress")} />
                <MyLink to="/ba3a/fea/readyReq" name={t("req stat read")} />
                <Divider noMargin />
                <MyLink to="/ba3a/fea/paymentDirectiveList" name={t("Payment directive list (VED)")} />
                <Divider noMargin />
                <MyLink to="/ba3a/fea/factoryManagement" name={t("factory cc")} isForVedBoss />
                <MyLink to="/ba3a/fea/vedSettingsGeneralNew" name={t("Settings (VED)")} />
              </NavItem>
              <NavItem name={t("LOGISTICIAN")} dataName={NAV_PATH_NAMES.LOGIST}>
                <MyLink to="/ba3a/logistician/sleeping-requests" name={t("Request Register for planning")} />
                <MyLink to="/ba3a/logistician/bos/tp-pool-preregister" name={t("Request Register (chief)")} />
                <MyLink to="/ba3a/logistician/tp-pool-register" name={t("Request Register in progress")} />
                <MyLink to="/ba3a/logistician/log-list-payment-to-tp" name={t("Payment directive list (TP)")} />
                <MyLink to="/ba3a/logistician/log-list-payment-to-sp" name={t("Payment directive list (SP)")} />
                <Divider noMargin />
                <MyLink to="/ba3a/logistician/tp-pool-factory" name={t("Process block factory")} />
                <MyLink to="/ba3a/logistician/tp-transportation" name={t("Transportation block (TP)")} />
                <MyLink to="/ba3a/logistician/warehouse" name={t("Process block 'Warehouse'")} />
                <Divider noMargin />
                <MyLink to="/ba3a/logistician/bos/final-destination" name={t("Final Destination (Boss)")} />
                <MyLink to="/ba3a/logistician/final-destination-Manager" name={t("Final destination (FD)")} />
                <Divider noMargin />
                <MyLink to="/ba3a/logistician/settings" name={t("Settings (Logist)")} />
              </NavItem>
              <NavItem name={t("FIN UNIT")} dataName={NAV_PATH_NAMES.FINBLOCK}>
                <MyLink to="/ba3a/fin-block/bankClient" name={t("Bank-Client")} />
                <Divider noMargin />
                <MyLink to="/ba3a/fin-block/exchangeRates" name={t("Exchange rates")} />
                <Divider noMargin />
                <MyLink to="/ba3a/fin-block/finBlockInvoiceList" name={t("Invoice List")} />
                <MyLink to="/ba3a/fin-block/canceledRequests" name={t("Canceled requests register")} />
                <MyLink to="/ba3a/fin-block/cashdeskDirective" name={t("Cashdesk directive")} />
                <MyLink to="/ba3a/fin-block/clientDirectiveList" name={t("Directives list (Client)")} />
                <MyLink to="/ba3a/fin-block/projectDirectiveList" name={t("Payment directives list (CO)")} />
                <MyLink to="/ba3a/fin-block/paymentDirectiveList" name={t("Payment directive list (VED)")} />
                <MyLink to="/ba3a/fin-block/paymentDirectiveListTP" name={t("Payment directive list (TP)")} />
                <MyLink to="/ba3a/fin-block/paymentDirectiveListStatement" name={t("Payment directive list (SP)")} />
                <MyLink to="/ba3a/fin-block/finBlockTransactionDayList" name={t("Daily operations register")} />
                <Divider noMargin />
                <MyLink to="/ba3a/fin-block/fin_settings" name={t("Settings (FinBlock)")} />
                <MyLink
                  //  to="/ba3a/fin-block/cashdesk"
                  name={t("Cashdesk")}
                  notAvailableStyle
                  onlyForSU
                />
                <MyLink
                  // to="/ba3a/fin-block/cashdeskOrders"
                  name={t("Cashdesk orders")}
                  notAvailableStyle
                  onlyForSU
                />
              </NavItem>
              <NavItem name={t("CASHDESK")} dataName={NAV_PATH_NAMES.CASHDESK}>
                <MyLink to={`/${NAV_PATH_NAMES.CASHDESK}/exchangeRates`} name={t("Exchange rates")} />
                <Divider noMargin />
                <MyLink to={`/${NAV_PATH_NAMES.CASHDESK}/directiveList`} name={t("Payment directives list (CO)")} />
                <MyLink to={`/${NAV_PATH_NAMES.CASHDESK}/invoiceList`} name={t("Invoice List")} />
                <MyLink to={`/${NAV_PATH_NAMES.CASHDESK}/cashdeskDirective`} name={t("Cashdesk directive")} />
                <MyLink to={`/${NAV_PATH_NAMES.CASHDESK}/transactionDayList`} name={t("Daily operations register")} />
                <Divider noMargin />
                <MyLink to={`/${NAV_PATH_NAMES.CASHDESK}/project`} name={t("Project")} />
                <MyLink
                  //  to="/ba3a/fin-block/cashdesk"
                  name={t("Cashdesk")}
                  notAvailableStyle
                  onlyForSU
                />
                <MyLink
                  // to="/ba3a/fin-block/cashdeskOrders"
                  name={t("Cashdesk orders")}
                  notAvailableStyle
                  onlyForSU
                />
              </NavItem>
              <NavItem name={t("FIN ACCOUNTING")} dataName={NAV_PATH_NAMES.FIN_ACCOUNTING} notAvailableStyle>
                <MyLink
                  // to="/ba3a/fin-accounting/accounts"
                  name={t("FIN ACCOUNTING")}
                  notAvailableStyle
                />
                <MyLink
                  // to="/ba3a/fin-accounting/accdir"
                  name={t("Account handbook")}
                  notAvailableStyle
                />
              </NavItem>
              <NavItem name={t("SERVICE")} dataName={NAV_PATH_NAMES.SERVICE} notAvailableStyle></NavItem>
              <NavItem name={t("WAREHOUSE")} dataName={NAV_PATH_NAMES.WAREHOUSE} notAvailableStyle>
                <MyLink
                  to={`${BASE_PATH}/${API_ENDPOINTS.WAREHOUSE.BASE}/${API_ENDPOINTS.WAREHOUSE.REQUESTS_FOR_PLANNING_REGISTER}`}
                  name={t("Request Register for planning")}
                />
                <div className="fcCol aistr w100">
                  <div className="nav-item" style={{paddingLeft: "0.75rem"}}>
                    {t("Warehouse inventory")}
                  </div>
                  <ul
                    className="fcCol"
                    style={{flexDirection: "column", alignItems: "flex-start", gap: "0px", paddingLeft: "1rem"}}
                  >
                    <li>
                      <MyLink
                        to={`${BASE_PATH}/${API_ENDPOINTS.WAREHOUSE.BASE}/${API_ENDPOINTS.WAREHOUSE.WAREHOUSE_INVENTORY_ORDER_CO}`}
                        name={t("Order / CO")}
                      />
                    </li>
                    <li>
                      <MyLink
                        to={`${BASE_PATH}/${API_ENDPOINTS.WAREHOUSE.BASE}/${API_ENDPOINTS.WAREHOUSE.WAREHOUSE_INVENTORY_RETAIL}`}
                        name={t("Retail")}
                      />
                    </li>
                  </ul>
                </div>
                <Divider noMargin />
                <MyLink
                  to={`${BASE_PATH}/${API_ENDPOINTS.WAREHOUSE.BASE}/${API_ENDPOINTS.WAREHOUSE.GOODS_RECEIVING_WAREHOUSE}`}
                  name={t("Receiving goods at the Warehouse")}
                />
                <Divider noMargin />
                <MyLink
                  to={`${BASE_PATH}/${API_ENDPOINTS.WAREHOUSE.BASE}/${API_ENDPOINTS.WAREHOUSE.WAREHOUSE_SETTINGS}`}
                  name={t("Warehouse Settings")}
                />
              </NavItem>
              {isITManager && (
                <NavItem
                  name={t("SYSTEM")}
                  dataName={NAV_PATH_NAMES.SYSTEM}
                  multiUser
                  uppercase
                  notAvailableStyle={!isITManager}
                >
                  {isITManager ? (
                    <MyLink
                      to={`/${BASE_PATH}/${API_ENDPOINTS.SYSTEM.BASE}/${API_ENDPOINTS.SYSTEM.TASKS_LIST}`}
                      name={t("Tasks list")}
                    />
                  ) : (
                    <div></div>
                  )}
                  {isITManager ? (
                    <MyLink
                      to={`/${BASE_PATH}/${API_ENDPOINTS.SYSTEM.BASE}/${API_ENDPOINTS.SYSTEM.ROUTINE_PROCEDURES}`}
                      name={t("Routine procedures")}
                    />
                  ) : (
                    <div></div>
                  )}
                </NavItem>
              )}

              <li className="mla">
                <FunctionButton
                  name="DevIT"
                  onClick={showDevItModal}
                  className={`nav-item ${devITNewTickets?.length > 0 && isITManager ? "accented" : ""}`}
                  style={{
                    marginLeft: "1rem",
                    marginRight: "0.5rem",
                    padding: "0.3rem 0.7rem",
                    backgroundColor: "aliceblue",
                  }}
                  autoWidth
                  uiPermissionType={UI_PERMISSION_TYPE.READABLE}
                />
              </li>
              <li>
                <NavLink
                  to="/ba3a/docs/bazaDocumentation"
                  end
                  className="nav-item b"
                  style={({isActive}) => (isActive ? activeStyle : undefined)}
                >
                  <ToolTip title={t("ba3a documentation")}>
                    <span className="navTitle" style={{display: "inline-block", paddingTop: "4px"}}>
                      <Icon name={ICON_NAMES.DOCS_PAGE} width={20} height={20} />
                    </span>
                  </ToolTip>
                </NavLink>
              </li>
              <NavItem
                name=""
                icon={<Icon name={ICON_NAMES.SETTING} width={20} height={20} />}
                dataName={NAV_PATH_NAMES.SETTINGS}
                multiUser
              >
                <div className="settings-menu-items">
                  {/* <MyLink to="/ba3a/settings/translate" name={t("Translation")} /> */}
                  <button
                    style={{border: "none", backgroundColor: "transparent", padding: 0}}
                    type="button"
                    className="nav-item b"
                    onClick={showTranslateModal}
                  >
                    {t("Translation")}
                  </button>
                  {isCoManager && (
                    <button
                      style={{border: "none", backgroundColor: "transparent", padding: 0}}
                      type="button"
                      className="nav-item b"
                      onClick={showCOHiddenColumnsModal}
                    >
                      {t("CO(f) settings")}
                    </button>
                  )}
                </div>
              </NavItem>
            </ul>
          </nav>
        )}

        <div className="navbar__right">
          <SwitchCustom
            checked={isUa}
            onChange={handleLangToggle}
            leftLabel="ENG"
            rightLabel="UA"
            ariaLabel="Language"
            className="navbar__lang-switch"
            scale={0.7}
          />
          <NavbarInfo />
        </div>
      </div>
    </div>
  );
};

export default NavBar;
